import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp, TypeBadge } from "./Sections";
import items from "@/content/insightsData.json";

const PAGES = [
  { title: "About HARC", to: "/about", keys: "about mission history people team board fellows living lab" },
  { title: "Approach", to: "/approach", keys: "approach how we work understand connect solve impact partners sectors" },
  { title: "Impact", to: "/impact", keys: "impact stories results outcomes projects case studies" },
  {
    title: "Impact Story: Strengthening the Alamo Area's Climate Plan",
    to: "/impact-story",
    keys: "alamo san antonio climate plan aacog emissions regional",
  },
  { title: "Insights & Resources", to: "/insights", keys: "insights resources articles reports podcasts news library" },
  { title: "Donate", to: "/donate", keys: "donate give support contribution philanthropy" },
  { title: "Careers", to: "/careers", keys: "careers jobs openings work positions hiring" },
  {
    title: "Start a Conversation",
    to: "/start-a-conversation",
    keys: "contact conversation enquiry challenge talk email phone",
  },
];

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#C9DA2D]/60 text-inherit">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [draft, setDraft] = useState(q);

  const pageHits = useMemo(
    () =>
      q
        ? PAGES.filter(
            (p) =>
              p.title.toLowerCase().includes(q.toLowerCase()) ||
              p.keys.includes(q.toLowerCase())
          )
        : [],
    [q]
  );

  const itemHits = useMemo(
    () =>
      q ? items.filter((i) => i.title.toLowerCase().includes(q.toLowerCase())) : [],
    [q]
  );

  const submit = (e) => {
    e.preventDefault();
    setParams(draft ? { q: draft } : {});
  };

  return (
    <Shell>
      <div className="mx-auto max-w-5xl px-6 py-16 md:py-24" data-testid="search-page">
        <FadeUp>
          <p className="mb-5 flex items-center text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
            <span className="mr-2.5 inline-block h-2.5 w-2.5 bg-[#FFA100]" aria-hidden="true" />
            Search
          </p>
          <h1 className="harc-heading text-4xl font-medium leading-[1.1] tracking-tight text-[#004778] md:text-5xl">
            Search HARC
          </h1>
          <form onSubmit={submit} className="mt-8" role="search">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B72]"
                aria-hidden="true"
              />
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Search pages, articles, reports, podcasts…"
                aria-label="Search HARC"
                data-testid="search-input"
                className="w-full border border-[#004778]/30 bg-white py-4 pl-12 pr-32 text-base text-[#022A42] focus:outline-none focus:ring-2 focus:ring-[#004778]"
              />
              <button
                type="submit"
                data-testid="search-submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[#FFA100] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
              >
                Search
              </button>
            </div>
          </form>
        </FadeUp>

        {q && (
          <div className="mt-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6B72]" data-testid="search-count">
              {pageHits.length + itemHits.length} result
              {pageHits.length + itemHits.length === 1 ? "" : "s"} for “{q}”
            </p>

            {pageHits.length > 0 && (
              <div className="mt-8">
                <h2 className="harc-heading text-xl font-medium text-[#004778]">Pages</h2>
                <div className="mt-3">
                  {pageHits.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="insight-row"
                      data-testid="search-page-result"
                    >
                      <span className="w-fit rounded-full border border-[#004778] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#004778]">
                        Page
                      </span>
                      <span className="harc-heading text-lg leading-snug text-[#022A42]">
                        {p.title}
                      </span>
                      <span />
                      <ArrowRight size={18} className="row-arrow text-[#FFA100]" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {itemHits.length > 0 && (
              <div className="mt-10">
                <h2 className="harc-heading text-xl font-medium text-[#004778]">
                  Insights & Resources
                </h2>
                <div className="mt-3" data-testid="search-item-results">
                  {itemHits.map((it) => (
                    <a
                      key={it.url}
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="insight-row"
                      data-testid="search-item-result"
                    >
                      <TypeBadge type={it.type} />
                      <span>
                        <span className="harc-heading block text-lg leading-snug text-[#022A42]">
                          <Highlight text={it.title} q={q} />
                        </span>
                        {it.summary && (
                          <span className="mt-1.5 block max-w-2xl text-sm font-normal leading-relaxed text-[#5C6B72]">
                            {it.summary}
                          </span>
                        )}
                        <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5C6B72]">
                          {it.topic}
                        </span>
                      </span>
                      <span className="text-xs uppercase tracking-[0.1em] text-[#5C6B72]">
                        {it.lastmod.slice(0, 4)}
                      </span>
                      <ArrowRight size={18} className="row-arrow text-[#FFA100]" aria-hidden="true" />
                    </a>
                  ))}
                  <div className="border-t border-[#004778]/20" aria-hidden="true" />
                </div>
              </div>
            )}

            {pageHits.length === 0 && itemHits.length === 0 && (
              <div
                className="mt-8 border border-dashed border-[#004778]/40 p-10 text-center"
                data-testid="search-empty"
              >
                <p className="harc-heading text-xl text-[#004778]">
                  No results for “{q}”.
                </p>
                <p className="mt-3 text-sm text-[#5C6B72]">
                  Try a broader term, or browse the{" "}
                  <Link to="/insights" className="font-semibold text-[#004778] underline">
                    Insights & Resources library
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
